import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Divider from '@mui/material/Divider';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { calcularPrecioConDescuento } from 'utils/descuentoUtils';

const Cart = ({ anchorEl, close, dataCart = [], removeItem, totalPrice, checkout }) => {
	const theme = useTheme(); // ✅ Usa el theme correctamente

	const obtenerDescuentos = (item) => item.descuentos || [];

	const getCartItem = (dataArray) =>
		dataArray.map((item, index) => {
			// Calcular precio con descuento para productos (no para packs)
			let precioFinal;
			let precioOriginal;
			let hayDescuento = false;

			if (item.tipo === 'pack') {
				// Para packs usar el precio ya calculado
				precioFinal = item.precioTotal;
			} else {
				// Para productos individuales, usar precioConDescuento si existe o calcularlo
				const descuentos = obtenerDescuentos(item);
				const precioBase = item.precio;
				const precioConDescuento =
					item.precioConDescuento || calcularPrecioConDescuento(precioBase, descuentos);
				const adicionalCosto = item.adicional?.costoAdicional || 0;

				precioOriginal = (precioBase + adicionalCosto) * item.cantidad;
				precioFinal = (precioConDescuento + adicionalCosto) * item.cantidad;
				hayDescuento = descuentos.length > 0;
			}

			return (
				<Fragment key={index.toString()}>
					<ListItem
						secondaryAction={
							<IconButton aria-label="Delete" onClick={() => removeItem(item)}>
								<DeleteIcon />
							</IconButton>
						}
					>
						<figure>
							<img
								src={
									item.imageSrc ||
									'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=500&q=80'
								}
								alt="thumb"
								style={{ width: 50, height: 50 }}
							/>
						</figure>
						<ListItemText
							primary={item.titulo}
							secondary={
								<>
									<span>
										Cantidad: {item.cantidad} Item
										{hayDescuento && (
											<>
												{' - '}
												<span style={{ textDecoration: 'line-through', color: '#ef4444' }}>
													${precioOriginal.toFixed(2)}
												</span>
											</>
										)}
										{' - '}
										<span style={{ fontWeight: 'bold' }}>${precioFinal.toFixed(2)}</span>
									</span>
									{item.adicional && (
										<Typography
											variant="body2"
											sx={{ fontStyle: 'italic', color: theme.palette.text.secondary }}
										>
											Adicional: {item.adicional.descripcion}{' '}
											{item.adicional.costoAdicional > 0 && `(+${item.adicional.costoAdicional})`}
										</Typography>
									)}
								</>
							}
							sx={{ marginLeft: theme.spacing(2) }}
						/>
					</ListItem>
					<li>
						<Divider />
					</li>
				</Fragment>
			);
		});

	return (
		<Menu
			id="cart-menu"
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={Boolean(anchorEl)}
			onClose={close}
			sx={{ minWidth: 300 }} // ✅ Corrige el error de estilos
		>
			<List
				component="ul"
				subheader={
					<ListSubheader component="div">
						<ShoppingCartIcon /> Total: {dataCart.length} productos en el carrito
					</ListSubheader>
				}
				sx={{ maxHeight: 400, overflowY: 'auto' }} // ✅ Se usa `sx` en lugar de clases obsoletas
			>
				{dataCart.length < 1 ?
					<div style={{ textAlign: 'center', padding: theme.spacing(2) }}>
						<Typography variant="subtitle1">Carrito vacío</Typography>
						<Typography variant="caption">Tus productos aparecerán aquí</Typography>
					</div>
				:	<Fragment>
						{getCartItem(dataCart)}
						<ListItem sx={{ fontWeight: 'bold' }}>
							<Typography variant="subtitle1">
								Total: <span>${totalPrice}</span>
							</Typography>
						</ListItem>
						<li>
							<Divider />
						</li>
						<ListItem>
							<Button fullWidth sx={{ marginTop: theme.spacing(1) }} onClick={checkout} color="secondary">
								Finalizar pedido
							</Button>
						</ListItem>
					</Fragment>
				}
			</List>
		</Menu>
	);
};

Cart.propTypes = {
	anchorEl: PropTypes.object,
	close: PropTypes.func.isRequired,
	dataCart: PropTypes.array.isRequired,
	removeItem: PropTypes.func.isRequired,
	checkout: PropTypes.func.isRequired,
	totalPrice: PropTypes.number.isRequired,
};

Cart.defaultProps = {
	anchorEl: null,
	dataCart: [],
};

export default Cart;
